import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

export default function AcknowledgeRisk() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(token ? "" : "Missing acknowledgement token. Please use the link from the escalation email.");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get(`/risk-ack/${token}`).then(({ data }) => setInfo(data)).catch((e) => setError(e?.response?.data?.detail || "This link is invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/risk-ack/${token}`, { note: note || null });
      setDone(data); toast.success("Escalation acknowledged");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not acknowledge"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="ack-page">
      <Card className="w-full max-w-md p-8 border-slate-200 shadow-none">
        <div className="mb-6"><Logo size="md" /></div>
        {info && <Link to={`/facilities/${info.facility_id}`} className="text-xs text-slate-500 font-mono-plex inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Open facility</Link>}
        <div className="mb-6">
          <div className="text-xs font-mono-plex uppercase tracking-widest text-rose-600 mb-2 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> At-risk escalation</div>
          <h2 className="font-heading text-3xl font-bold">Acknowledge</h2>
          <p className="text-sm text-slate-500 mt-2">Confirm you have picked this up. The rep will see your acknowledgement on the facility.</p>
        </div>
        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="ack-error">{error}</div>}
        {done ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2" data-testid="ack-done">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> Acknowledged for <b>{done.facility_name}</b>. The rep has been notified in the CRM.
          </div>
        ) : info && (
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm" data-testid="ack-facility">
              <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{info.facility_name}</span>{info.city && <span className="text-slate-500">· {info.city}</span>}<Badge className="bg-rose-600 hover:bg-rose-600 text-[10px]">{info.level_label}</Badge></div>
              <div className="text-xs text-slate-700 mt-1">{info.risk_reason}</div>
              <div className="text-[11px] text-slate-500 font-mono-plex mt-1">Escalated by {info.escalated_by} · {(info.escalated_at || "").slice(0, 10)}</div>
              {info.already_acknowledged && <div className="text-[11px] text-emerald-700 mt-1">Already acknowledged by {info.already_acknowledged.by_name}</div>}
            </div>
            <div>
              <Label htmlFor="note">Note for the rep (optional)</Label>
              <Textarea id="note" rows={3} data-testid="ack-note" placeholder="Let's review on Thursday's call…" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy} data-testid="ack-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? "Saving…" : `Acknowledge as ${info.acknowledger || "leadership"}`}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
